import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Users, MapPin, Clock, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '@/components/SEO';

const SlowCooking = () => {
  const navigate = useNavigate();

  const handleBooking = () => {
    navigate('/');
    setTimeout(() => window.scrollTo(0, 0), 100);
  };

  const handleGoBack = () => {
    navigate('/#upcoming-experiences');
  };

  return (
    <>
      <SEO
        title="Slow Cooking Circle - Mindful Food Preparation"
        description="5-hour slow cooking workshop. Transform meal preparation into meditation with seasonal ingredients and mindful techniques."
        canonicalUrl="https://mysticforest.club/experiences/slow-cooking"
      />
      
      <div className="min-h-screen bg-background">
        <Navigation />
      
      {/* Go Back Button */}
      <div className="pt-20 pb-4">
        <div className="max-w-6xl mx-auto px-6">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Experiences
          </Button>
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="relative py-32">
        <div className="absolute inset-0">
          <img 
            src="/lovable-uploads/8c982883-9933-410d-8994-60cccb893f4a.png" 
            alt="Slow Cooking Workshop"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-serif font-light mb-6">
            Slow Cooking Circle
          </h1>
          <p className="text-xl md:text-2xl mb-8 opacity-90">
            Rediscover the sacred art of mindful food preparation
          </p>
          <Button size="lg" className="px-8 py-4 text-lg" onClick={handleBooking}>
            Join the Kitchen
          </Button>
        </div>
      </section>

      {/* Quick Info */}
      <section className="py-16 bg-card">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div className="flex flex-col items-center">
              <Calendar className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">When</h3>
              <p className="text-muted-foreground">March 23, 2024</p>
              <p className="text-sm text-muted-foreground">10 AM - 3 PM</p>
            </div>
            <div className="flex flex-col items-center">
              <MapPin className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">Where</h3>
              <p className="text-muted-foreground">Forest Kitchen Studio</p>
            </div>
            <div className="flex flex-col items-center">
              <Users className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">For Whom</h3>
              <p className="text-muted-foreground">Food lovers & mindfulness seekers</p>
            </div>
            <div className="flex flex-col items-center">
              <Clock className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">Duration</h3>
              <p className="text-muted-foreground">5 Hours</p>
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-serif font-light mb-8 text-center">
            Cooking as Sacred Practice
          </h2>
          <div className="prose prose-lg max-w-none text-muted-foreground">
            <p className="mb-6">
              In our fast-paced world, cooking has become another rushed task to complete. But what if preparing food could be a meditation? What if the kitchen could become a sacred space for presence, creativity, and nourishment?
            </p>
            <p className="mb-6">
              Our Slow Cooking Circle invites you to rediscover the ancient art of mindful food preparation. Working with seasonal, local ingredients in our beautiful forest kitchen, you'll learn to cook with all your senses, creating meals that nourish both body and soul.
            </p>
            <p className="mb-6">
              This isn't a traditional cooking class – it's a journey into the meditative aspects of food preparation, the joy of communal cooking, and the magic that happens when we slow down enough to truly connect with our ingredients.
            </p>
          </div>
        </div>
      </section>

      {/* The Experience */}
      <section className="py-16 bg-card">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-serif font-light mb-12 text-center">
            The Day's Journey
          </h2>
          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>Mindful Ingredient Selection (30 min)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Begin by mindfully selecting ingredients from our garden and local suppliers. Learn to choose with all your senses.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Knife Meditation & Prep (90 min)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Transform vegetable preparation into moving meditation. Learn proper knife techniques while staying fully present with each cut, chop, and slice.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Slow Cooking Techniques (120 min)</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>Traditional slow-braising methods</li>
                  <li>Bread making as meditation</li>
                  <li>Soup creation with patience and intuition</li>
                  <li>Preserving techniques for seasonal eating</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Communal Feast & Integration (60 min)</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Share the meal we've created together in gratitude and presence. Discuss how to integrate slow cooking principles into daily life.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-serif font-light mb-12 text-center">
            What You'll Discover
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <CardTitle>Mindful Cooking Techniques</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Learn to cook with presence, using all your senses to guide timing and flavoring rather than just following recipes.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Seasonal Eating Wisdom</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Understand how to eat in harmony with natural cycles, choosing ingredients that support your body's changing needs.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Stress-Free Kitchen Practices</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Transform your kitchen into a sanctuary rather than a source of stress through mindful organization and intentional practices.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Community Building Through Food</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Experience how sharing food preparation creates deeper connections than just sharing the finished meal.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What You'll Take Home */}
      <section className="py-16 bg-card">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-serif font-light mb-12 text-center">
            Take Home
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">Recipes & Knowledge</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Handwritten recipe collection</li>
                <li>• Seasonal cooking calendar</li>
                <li>• Mindful cooking practice guide</li>
                <li>• Local supplier contacts</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">Physical Goods</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Fresh bread you baked</li>
                <li>• Homemade preserves</li>
                <li>• Herbal tea blend</li>
                <li>• Starter for future bread making</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Investment */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-serif font-light mb-8">Investment</h2>
          <Card className="p-8">
            <div className="text-4xl font-semibold mb-4">$165</div>
            <p className="text-muted-foreground mb-6">Per person</p>
            <div className="text-sm text-muted-foreground mb-8">
              <p>Includes all ingredients, lunch, take-home items</p>
              <p>Aprons and tools provided</p>
              <p>Recipe collection and follow-up resources</p>
            </div>
            <Button size="lg" className="w-full" onClick={handleBooking}>
              Reserve Your Spot
            </Button>
          </Card>
        </div>
      </section>

        <Footer />
      </div>
    </>
  );
};

export default SlowCooking;